/**
 * Entry point: renderer + fixed-timestep loop + input.
 *
 * WORLD ADAPTER. Everything the renderer needs from the simulation is behind
 * the `WorldView` interface below, and the concrete world comes from one
 * factory (`createWorld`). The real `src/sim/world.ts` is being written
 * separately; swapping it in is this, and only this:
 *
 *     import { World } from './sim/world';
 *     const createWorld = (): WorldView => new World();   // or a thin wrapper
 *
 * Until then a `LocalWorld` composed from the physics modules drives the scene,
 * so the renderer is developed against the real physics rather than a canned
 * animation.
 *
 * INTERPOLATION. The simulation runs at exactly 120 Hz; the display does not.
 * Every visual quantity is interpolated between the previous and current tick
 * with the loop's `alpha` — positions by lerp, orientations by slerp. Reading
 * the live physics state each frame instead is the classic mistake, and it
 * shows up as a shimmer on the car at every refresh rate that is not exactly
 * 120 Hz.
 */

import { PerspectiveCamera, Quaternion, Vector3 } from 'three';
import { GOAL } from './physics/arena-data';
import { DT } from './physics/constants';
import { MatchState, type MatchPhase } from './game/match';
import { MatchStatsCollector, type StatTickEvents } from './game/match-stats';
import {
  BINDABLE_ACTIONS,
  carBodyForTeam,
  cloneBindings,
  loadSettings,
  matchConfigFor,
  type GameSettings,
} from './game/settings';
import { botRosterFor } from './game/bots';
import { DRILL_ATTEMPTS, DrillSession, drillMatchConfig } from './game/drills';
import { BotDirector, viewOf, type BotWorldView } from './bot/bot';
import type { BallPredictionEvents } from './bot/prediction';
import { InputController, type KeyBindings } from './input/controller';
import { TouchControls } from './input/touch';
import { pointerCapabilities, touchControlsVisible } from './input/touch-model';
import { FixedLoop } from './render/loop';
import { World, type WorldOptions } from './sim/world';
import type { ArenaGeometry } from './physics/surface';
import { makeBall, type BallState } from './physics/ball';
import { makeCar, neutralInput, type CarInput, type CarState } from './physics/car-state';
import {
  clone,
  copy,
  dot,
  forward as carForward,
  length,
  type Vec3,
} from './physics/math';
import { GameCamera } from './render/camera';
import { fromThree, lerpToThree, orientationToQuaternion, toThree } from './render/convert';
import { AudioEngine } from './audio/engine';
import { impactStrength } from './audio/synth';
import { GameScene } from './render/scene';
import { buildCar, type CarMesh } from './render/car-mesh';
import { EffectsSystem, type CarEffectState } from './render/effects';
import { Hud, ballPointer, type BallPointer, type DrillHudState } from './render/hud';
import { HeldKey, scoreboardMarkup, type ScoreboardEntry } from './render/scoreboard';
import {
  ReplayBuffer,
  SkipInput,
  SkipLatch,
  makeReplayPose,
  playbackFrame,
  replayWindow,
  scorerLabel,
  type ReplayPose,
  type ReplayWindow,
} from './render/replay';
import { ReplayCamera } from './render/replay-camera';
import { MenuOverlay, type MatchHooks, type MatchSession, type SessionOptions } from './ui/menu';
import type { MatchResult } from './ui/menu-model';
import { NetHud } from './ui/net-hud';
import type { NetPlayerRow } from './ui/net-model';
import type { NetClient } from './net/client';
import { CAR_BODY_IDS, type CarBodyId } from './render/car-body';
import type { CarBody } from './physics/car-config';

// ---------------------------------------------------------------------------
// The interface the renderer needs from a world
// ---------------------------------------------------------------------------

export interface BallHitEvent {
  /** Contact point, PHYSICS frame. */
  position: Vec3;
  /** Closing speed, uu/s — drives how big the impact flash is. */
  strength: number;
}

export interface WorldEvents {
  goal: { team: 0 | 1; speed: number } | null;
  hits: BallHitEvent[];
  /** True on the tick the world was reset to a kickoff. */
  kickoff: boolean;
  /** True when the tick was frozen and nothing moved. */
  frozen: boolean;
  /**
   * CAR-ball contacts this tick, which is not the same as `hits.length`: `hits`
   * also carries inferred ball-vs-arena impacts, and those are on the predicted
   * trajectory already. Feeding them to the ball predictor would throw its
   * cache away on every hard wall bounce for no reason.
   */
  touches: number;
  /**
   * The tick's raw record, for the match-statistics collector.
   *
   * OPTIONAL, and absent for a networked world. `NetWorldView` in
   * src/net/client.ts also implements `WorldView` and has no such record to
   * give — the server owns the authoritative events — so a required field here
   * would either break that implementation or invite it to invent one. Absent
   * means "no progression from this match", which is the honest answer for a
   * client that cannot be trusted with its own statistics.
   */
  detail?: StatTickEvents;
}

export interface WorldView {
  readonly ball: BallState;
  readonly cars: readonly CarState[];
  readonly match: MatchState;
  /** Ticks simulated so far. The bots' prediction cache is keyed on it. */
  readonly tick: number;
  /** The collision mesh, for the bots' ball prediction. */
  readonly arena: ArenaGeometry;
  /** Whether boost pad `index` (RLBot FieldInfo order) is currently available. */
  padActive(index: number): boolean;
  /** Analog powerslide value of a car, 0..1, for the tyre marks. */
  powerslide(index: number): number;
  /** Cut the celebration/replay short. Returns the phase entered, or null. */
  skipReplay(): MatchPhase | null;
  step(inputs: readonly CarInput[], dt: number, tick: number): WorldEvents;
}

// ---------------------------------------------------------------------------
// Adapter over the real simulation
// ---------------------------------------------------------------------------

/**
 * `World` (src/sim/world.ts) owns the authoritative tick order, goal
 * detection, demolitions and match flow, and is covered by its own suite. This
 * adapter exists only to reshape its event record into what the renderer wants
 * — it must never contain physics of its own. An earlier placeholder here
 * duplicated the per-car step and silently drifted from the tested version
 * (it double-integrated orientation while wheels were in contact); that is the
 * failure mode this thin-wrapper rule exists to prevent.
 */

/** Impact speed above which a ball-vs-world contact is worth a flash. */
const WORLD_HIT_FLASH_SPEED = 900;

class WorldAdapter implements WorldView {
  private readonly world: World;

  constructor(options: WorldOptions) {
    this.world = new World(options);
  }

  /** Ball speed at the end of the previous tick, for the debug overlay. */
  private lastBallSpeed = 0;

  get ball(): BallState {
    return this.world.ball;
  }

  get cars(): readonly CarState[] {
    return this.world.cars;
  }

  get match(): MatchState {
    return this.world.match;
  }

  get tick(): number {
    return this.world.tick;
  }

  get arena(): ArenaGeometry {
    return this.world.arena;
  }

  padActive(index: number): boolean {
    return this.world.padActive(index);
  }

  powerslide(index: number): number {
    return this.world.powerslide(index);
  }

  skipReplay(): MatchPhase | null {
    return this.world.skipReplay();
  }

  /**
   * Put the field back to a kickoff. Not on `WorldView`, deliberately: only a
   * LOCAL world can be told to do this, and a networked one that offered it
   * would be offering a client the ability to reset the server's room. The one
   * caller is the drill runner, which is offline by construction.
   */
  resetForKickoff(): void {
    this.world.resetForKickoff();
  }

  step(inputs: readonly CarInput[], dt: number): WorldEvents {
    const speedBefore = length(this.world.ball.velocity);
    const stepped = this.world.step(inputs, dt);

    const hits: BallHitEvent[] = stepped.ballTouches.map(touch => ({
      position: clone(touch.position),
      strength: touch.relativeSpeed,
    }));

    // The world reports car-ball contacts but not ball-vs-arena ones, so a hard
    // wall or ceiling bounce would produce no flash and no impact sound. Infer
    // it: a large speed change on a tick with no car contact can only have come
    // from the arena. Cheap, and it cannot fire during ordinary rolling.
    //
    // A GOAL is excluded because it is not a bounce: `freezeScoringPose` stops
    // the ball dead on the tick it crosses, so the inferred "impact" would be
    // the entire shot speed and would fire a phantom flash and camera shake on
    // top of the explosion that is already playing.
    const speedAfter = length(this.world.ball.velocity);
    if (hits.length === 0 && !stepped.frozen && !stepped.goal) {
      const impact = Math.abs(speedAfter - speedBefore);
      if (impact > WORLD_HIT_FLASH_SPEED) {
        hits.push({ position: clone(this.world.ball.position), strength: impact });
      }
    }
    this.lastBallSpeed = speedAfter;

    return {
      goal: stepped.goal ? { team: stepped.goal.team, speed: stepped.goal.speed } : null,
      hits,
      kickoff: stepped.kickoff,
      frozen: stepped.frozen,
      touches: stepped.ballTouches.length,
      // `WorldStepEvents` satisfies `StatTickEvents` structurally; see the
      // header of src/game/match-stats.ts on why that file does not import
      // this one's world.
      detail: stepped,
    };
  }

  /** Ball speed at the end of the last tick, for the debug overlay. */
  ballSpeed(): number {
    return this.lastBallSpeed;
  }
}

const createWorld = (options: WorldOptions): WorldAdapter => new WorldAdapter(options);

/**
 * The renderable body for a seat.
 *
 * The wire carries all seven `CarBody` values (the hitboxes the physics knows),
 * and only two of them have meshes. An unmodelled body falls back to the octane
 * mesh rather than to nothing — the car is still there, still that hitbox, and
 * still collides exactly as the server says; it simply looks like an octane.
 * Refusing to draw it would hide a real car on the pitch.
 */
const renderableBody = (body: CarBody): CarBodyId =>
  CAR_BODY_IDS.includes(body as CarBodyId) ? (body as CarBodyId) : 'octane';

// ---------------------------------------------------------------------------
// Interpolation snapshots
// ---------------------------------------------------------------------------

interface CarSnapshot {
  position: Vec3;
  quaternion: Quaternion;
}

const snapshotCar = (car: CarState): CarSnapshot => ({
  position: clone(car.position),
  quaternion: orientationToQuaternion(car.orientation),
});

const writeSnapshot = (into: CarSnapshot, car: CarState): void => {
  copy(into.position, car.position);
  orientationToQuaternion(car.orientation, into.quaternion);
};

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

/**
 * Seed for the world's generator and, through it, for every bot's.
 *
 * A constant rather than a clock reading: a match played from the same setup
 * with the same inputs must unfold identically, which is what makes a bug
 * reproducible from a description. Nothing about the play is repetitive as a
 * result — the human is the entropy.
 */
const MATCH_SEED = 0x52574232;

/**
 * One match. Built by the menu (see `MatchSession` in src/ui/menu.ts) and
 * thrown away when the match is left. Everything that varies between matches —
 * roster size, clock, which car is yours, which cars are bots, camera and key
 * bindings — arrives as `GameSettings` and is read exactly once, here.
 *
 * BOT INPUTS. One `BotDirector` drives every car the human is not in (see
 * ./game/bots for which those are). It is asked for the whole roster's inputs
 * ONCE per tick, which is what keeps the ball prediction shared: the director
 * owns the predictor, and a per-car call would recompute a 720-tick trajectory
 * per bot per tick. `observe` hands last tick's events back so the cache
 * invalidates on a touch, a kickoff or a goal and on nothing else.
 */
class App implements MatchSession {
  private readonly world: WorldView;
  /**
   * The same object as `world` when this session owns its simulation, and null
   * when the world is a networked prediction. Exists for the one operation a
   * client must not have — resetting the field. See `WorldAdapter`.
   */
  private readonly localWorld: WorldAdapter | null;
  private readonly input: InputController;
  /**
   * The on-screen controls.
   *
   * BUILT UNCONDITIONALLY, and shown or hidden from `settings.touch`. The
   * alternative — constructing them only on a touch device — makes the
   * Controls screen's `Always On` row a setting that cannot take effect
   * without restarting the match, and costs a stylesheet and ten divs to
   * avoid. `setVisible` is what everything downstream reads, including
   * `InputController`, so there is one answer to "are the touch controls
   * driving" rather than two that can disagree.
   */
  private readonly touch: TouchControls;
  /** What the SETTING wants, before the pause menu's veto. */
  private touchWanted: boolean;
  private readonly scene: GameScene;
  private readonly camera: GameCamera;
  private readonly effects: EffectsSystem;
  private readonly hud: Hud;
  private readonly carMeshes: CarMesh[] = [];
  private readonly loop: FixedLoop;

  // --- audio ---
  /**
   * The whole of the game's sound. Nothing is loaded: every voice is
   * synthesized (see src/audio/synth.ts), so this costs one AudioContext and
   * no network.
   */
  private readonly audio = new AudioEngine();
  /** Cleared once the first gesture has started the context. See `unlockAudio`. */
  private audioUnlock: (() => void) | null = null;
  /** Last countdown numeral announced, so each pip is played exactly once. */
  private lastCountdownSpoken = -1;
  /** Last boost reading per car, for detecting a pad pickup by its jump. */
  private readonly lastBoost: number[];
  /** Whether each car was on the ground last tick, for jump/landing edges. */
  private readonly wasOnGround: boolean[];
  /** Dodge and demolition are both edges too; same reason, same shape. */
  private readonly wasDodging: boolean[];
  private readonly wasDemolished: boolean[];
  /** Scratch for the listener orientation; `setListener` takes physics-frame. */
  private readonly listenerForward = new Vector3();
  private readonly listenerUp = new Vector3();

  // Previous/current physics samples, for the alpha interpolation. Two fixed
  // buffers per quantity, swapped each tick — no per-tick allocation.
  private readonly prevCars: CarSnapshot[];
  private readonly currCars: CarSnapshot[];
  private prevBall: Vec3;
  private currBall: Vec3;

  /** Visual ball spin, integrated from angular velocity. `BallState` carries no
   *  orientation because the physics does not need one. */
  private readonly ballSpin = new Quaternion();
  private readonly spinDelta = new Quaternion();

  /** Scratch states handed to the camera, holding the interpolated transform. */
  private readonly interpCar: CarState;
  private readonly interpBall: BallState;

  /**
   * Inputs actually applied last tick, parallel to `world.cars`, so the render
   * pass can show each car's own steering and boost — a bot flooring it now
   * gets a flame and turned wheels, which it would not if only the player's
   * input were kept.
   */
  private lastInputs: CarInput[];

  private readonly vecA = new Vector3();
  private readonly vecB = new Vector3();
  private readonly nozzle = new Vector3();
  /** Scratch for projecting the ball into clip space; `project` mutates. */
  private readonly ballNdc = new Vector3();

  /** Index into `world.cars` of the car the camera and HUD follow. */
  private readonly player: number;
  /** Index of the car the keyboard drives, or null while spectating. */
  private readonly human: number | null;
  private readonly team: 0 | 1;

  /**
   * The connection, when this is a networked match.
   *
   * Its ONLY effects on this class are the three below: the world comes from
   * the prediction, the car bodies come from the roster, and the pause menu
   * stops the local input rather than the simulation. Everything else — scene,
   * camera, HUD, effects, replay buffer, the fixed loop — is the same object
   * graph the offline game builds, which is what "the two sessions share the
   * renderer completely" has to mean to be worth saying.
   */
  private readonly net: NetClient | null;
  /** The connection indicator and ping scoreboard. Networked matches only. */
  private readonly netHud: NetHud | null;

  /**
   * The match's statistics, folded in one tick at a time.
   *
   * Null for a networked match, where `WorldEvents.detail` is absent — see the
   * note on that field. Everything it produces is the LOCAL player's, read out
   * of it once at full time; nothing on the hot path reads it back.
   */
  private readonly stats: MatchStatsCollector | null;

  /**
   * The practice run, or null for an ordinary match.
   *
   * Owns the attempts, the clock, the tally AND the placement — everything
   * except what only a renderer can do, which is the two lines in `stepDrill`
   * that throw away the last attempt's particles. See src/game/drills.ts.
   */
  private readonly drill: DrillSession | null;

  /** Null only if the mode somehow leaves every car to the human. */
  private readonly director: BotDirector | null;
  private readonly botView: BotWorldView;
  /**
   * Reused across ticks: the director is fed this same object every tick, so a
   * per-tick allocation on the hot path is avoided. `ballTouches` is a mutable
   * length carrier for the same reason.
   */
  private readonly predictionEvents: BallPredictionEvents & {
    ballTouches: { length: number };
  } = { ballTouches: { length: 0 }, kickoff: false, frozen: false, goal: null };

  /**
   * The live binding map. `InputController` captures it by reference and never
   * replaces it, so `applySettings` MUTATES this object rather than assigning a
   * new one — that is what makes a rebind from the pause menu take effect
   * without rebuilding the controller.
   */
  private readonly bindings: KeyBindings;

  /** Set by the menu. Rendering continues, so the paused world is its backdrop. */
  private paused = false;
  private lastPhase: MatchPhase;
  private lastBallCam: boolean;
  private readonly onResize: () => void;

  // --- replay ---
  /** Rolling pose recorder. Fed one frame per LIVE tick; cleared on kickoff. */
  private readonly replayBuffer: ReplayBuffer;
  /** Where the sampled frame is written. One object for the App's whole life. */
  private readonly replayPose: ReplayPose;
  private readonly replayCamera = new ReplayCamera();
  private readonly skipLatch = new SkipLatch();
  private readonly skipInput = new SkipInput(window);
  /** Hold Tab for the match scoreboard. Offline only — see `updateHud`. */
  private readonly scoreboardKey = new HeldKey(window, 'Tab');
  /** The goal being replayed, or null outside the replay phase. */
  private clip: {
    window: ReplayWindow;
    scorer: string;
    speed: number;
  } | null = null;

  constructor(
    container: HTMLElement,
    settings: GameSettings,
    private readonly hooks: MatchHooks,
    options: SessionOptions = {}
  ) {
    const setup = settings.setup;
    const net = options.net ?? null;
    this.net = net;
    // The networked world is the PREDICTED one — see `NetWorldView` in
    // src/net/client.ts. It satisfies `WorldView`, so this assignment is the
    // entire difference between the two kinds of session as far as everything
    // below is concerned.
    const netView = net?.view ?? null;

    if (netView !== null) {
      this.world = netView;
      this.localWorld = null;
      // The seat the server gave us, which may be null: a room that was full
      // seats a joiner in the stands, and a spectator still needs a camera.
      this.human = netView.seatIndex;
      this.player = netView.seatIndex ?? 0;
      this.team = this.world.cars[this.player]?.team ?? 0;
      this.director = null;
    } else {
      // `options.learnedPolicy` is the entire wiring for a trained policy: the
      // roster puts one on `BotConfig.policy` and `BotDirector` drives with it
      // instead of the scripted decision layer. Nothing else in this file knows
      // the difference, which is what src/bot/difficulty.ts means by a seam.
      const roster = botRosterFor(setup, MATCH_SEED, options);
      this.localWorld = createWorld({
        teamSize: roster.teamSize,
        // Free play is a one-car roster, which `teamSize` alone cannot express
        // — see `botRosterFor`. Every other mode is symmetric and takes the
        // default path, so the explicit list is built only when it differs.
        ...(roster.carCount === roster.teamSize * 2
          ? {}
          : { cars: roster.teams.map(team => makeCar(team)) }),
        // A drill overrides the whole match configuration rather than adjusting
        // it: its phase durations are what stop `World` re-asserting a kickoff
        // pose over each placed shot. See the note on `drillMatchConfig`.
        matchConfig: options.drill === undefined ? matchConfigFor(setup) : drillMatchConfig(),
        seed: MATCH_SEED,
      });
      this.world = this.localWorld;
      this.player = roster.cameraCarIndex;
      this.human = roster.humanCarIndex;
      this.team = setup.team;
      this.director =
        roster.configs.length > 0 ? new BotDirector(this.world.arena, roster.configs) : null;
    }

    // Built AFTER the world, because constructing it places the first shot.
    // Never for a networked session: `localWorld` is null there, and a drill
    // is offline by construction.
    this.drill =
      options.drill === undefined || this.localWorld === null
        ? null
        : new DrillSession(options.drill, Math.round(1 / DT), this.localWorld);

    this.lastPhase = this.world.match.phase;
    this.lastInputs = this.world.cars.map(() => neutralInput());
    this.botView = viewOf(this.world);
    // Offline only: a networked world reports no tick detail, and a client that
    // scored its own progression would be a client that could edit it.
    this.stats = netView === null ? new MatchStatsCollector(this.world.cars) : null;

    this.bindings = cloneBindings(settings.bindings);
    this.input = new InputController(this.bindings);
    this.input.ballCam = settings.ballCam;
    this.lastBallCam = settings.ballCam;

    this.touch = new TouchControls(container, {
      onPause: () => this.hooks.onPauseRequested(),
      autoThrottle: settings.touch.autoThrottle,
    });
    this.touch.setSpectating(this.human === null);
    this.input.setTouchSource(this.touch);
    this.touchWanted = touchControlsVisible(settings.touch.mode, pointerCapabilities());

    this.camera = new GameCamera({ settings: settings.camera });

    this.scene = new GameScene({ container });
    this.hud = new Hud(container, this.bindings);
    // The default 6,000 covers the boost trails and the ball's own explosion.
    // The fireworks add up to ~2,000 concurrent sparks on top of it (see
    // PARTICLE_BUDGET in ./render/fireworks), and a pool that recycles under a
    // goal display drops exactly the sparks the player is looking at.
    this.effects = new EffectsSystem({
      seed: 0x1d0d0f,
      carCount: this.world.cars.length,
      particleCapacity: 8000,
    });
    this.scene.scene.add(this.effects.group);

    this.replayBuffer = new ReplayBuffer(this.world.cars.length);
    this.replayPose = makeReplayPose(this.world.cars.length);

    this.lastBoost = this.world.cars.map(car => car.boostAmount);
    this.wasOnGround = this.world.cars.map(car => car.onGround);
    this.wasDodging = this.world.cars.map(car => car.isDodging);
    this.wasDemolished = this.world.cars.map(car => car.demolished);
    this.audio.mixer.setMasterVolume(settings.audio.volume);
    this.audio.setMuted(settings.audio.muted);
    this.unlockAudio();

    // Bodies come from the ROOM in a networked match: every player picked their
    // own, so the local rule "your team drives your car, theirs drives the
    // other" (see `carBodyForTeam`) does not apply and would draw half the
    // pitch wrong.
    //
    // THE LOADOUT GOES ON ONE CAR. `options.playerLivery` is the human's, and
    // only the human's seat wears it: a pitch where every car carried the
    // player's decal would make their own car harder to find, and the team
    // colour is the read that has to survive. Everyone else gets the stock team
    // car, which is what `buildCar` builds when it is given no livery.
    const livery = options.playerLivery ?? null;
    for (let i = 0; i < this.world.cars.length; i++) {
      const car = this.world.cars[i];
      const mine = livery !== null && i === this.human;
      const body = mine
        ? livery.body
        : netView !== null
          ? renderableBody(netView.bodyFor(i))
          : carBodyForTeam(setup, car.team);
      const mesh = buildCar(car.team, body, mine ? livery : undefined);
      this.carMeshes.push(mesh);
      this.scene.scene.add(mesh.group);
      // The equipped boost and trail, handed over ONCE: a style is a property of
      // the loadout and the loadout cannot change during a match, so pushing it
      // per frame would be sixty copies a second of a constant.
      this.effects.setCarStyle(i, {
        core: mesh.boost.core,
        supersonicCore: mesh.boost.supersonicCore,
        hot: mesh.boost.hot,
        cool: mesh.boost.cool,
        length: mesh.boost.length,
        width: mesh.boost.width,
        flicker: mesh.boost.flicker,
        rate: mesh.boost.rate,
        size: mesh.boost.size,
        growth: mesh.boost.growth,
        life: mesh.boost.life,
        buoyancy: mesh.boost.buoyancy,
        trailHot: mesh.trail.hot,
        trailCool: mesh.trail.cool,
        trailRate: mesh.trail.rate,
        trailLife: mesh.trail.life,
        trailSize: mesh.trail.size,
      });
    }

    this.netHud = net === null ? null : new NetHud(container);

    this.prevCars = this.world.cars.map(snapshotCar);
    this.currCars = this.world.cars.map(snapshotCar);
    this.prevBall = clone(this.world.ball.position);
    this.currBall = clone(this.world.ball.position);

    this.interpCar = makeCar(this.world.cars[this.player].team);
    this.interpBall = makeBall();

    this.camera.reset(this.world.cars[this.player], this.world.ball, this.input.ballCam);

    this.loop = new FixedLoop({
      tick: (dt, tickIndex) => this.tick(dt, tickIndex),
      render: (alpha, frameTime) => this.render(alpha, frameTime),
    });

    this.onResize = () => this.scene.resize(this.camera.camera);
    window.addEventListener('resize', this.onResize);
    this.scene.resize(this.camera.camera);
    this.syncTouch();

    // The camera was reset above against a kickoff pose that the drill's own
    // first shot has since replaced. Without this the opening frame of every
    // drill is a swing from the centre spot to wherever the shot put the car.
    if (this.drill !== null) {
      this.camera.reset(this.world.cars[this.player], this.world.ball, this.input.ballCam);
    }
  }

  /**
   * Push `touchWanted` and the pause state onto the overlay and the HUD.
   *
   * The overlay goes away while the pause menu is up — it sits under that menu
   * and a stick behind a settings panel is a stick that swallows presses meant
   * for the panel. The HUD LAYOUT does not follow it: the menu is translucent,
   * the pitch shows through, and a boost ring that jumped corners every time
   * the player opened and closed a menu would be motion with no meaning.
   */
  private syncTouch(): void {
    this.touch.setVisible(this.touchWanted && !this.paused);
    this.hud.setTouchLayout(this.touchWanted);
  }

  start(): void {
    this.loop.start();
  }

  // -------------------------------------------------------------------------
  // MatchSession — the surface the menu drives
  // -------------------------------------------------------------------------

  setPaused(paused: boolean): void {
    this.paused = paused;
    // The tick is what drains the skip latch, and a paused App does not tick.
    // Without this, every key pressed in the pause menu is still waiting when
    // play resumes and eats the next replay.
    this.skipInput.clear();
    this.syncTouch();
  }

  /**
   * Start the audio context on the first user gesture.
   *
   * Browsers refuse to run an AudioContext created outside one, and a match
   * can begin from a keypress the menu handled — so the listeners go on
   * `window` in the capture phase and remove themselves the moment the context
   * is actually running. `once` is not enough on its own: a gesture that
   * arrives while the context is still resuming would otherwise be the only
   * one we ever get.
   */
  private unlockAudio(): void {
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    const tryResume = (): void => {
      void this.audio.resume().then(running => {
        if (running) this.releaseAudioUnlock();
      });
    };
    for (const name of events) {
      window.addEventListener(name, tryResume, { capture: true, passive: true });
    }
    this.audioUnlock = () => {
      for (const name of events) {
        window.removeEventListener(name, tryResume, { capture: true });
      }
    };
    // The context may already be running — a second match in the same session
    // inherits a live one from the shared mixer.
    tryResume();
  }

  private releaseAudioUnlock(): void {
    this.audioUnlock?.();
    this.audioUnlock = null;
  }

  applySettings(settings: GameSettings): void {
    this.camera.setSettings(settings.camera);
    this.audio.mixer.setMasterVolume(settings.audio.volume);
    this.audio.setMuted(settings.audio.muted);
    for (const action of BINDABLE_ACTIONS) {
      this.bindings[action] = [...settings.bindings[action]];
    }
    // The hint strip is rendered from the bindings, so it has to be told.
    this.hud.setBindings(this.bindings);
    // Only on an actual change, so nudging a camera slider mid-match does not
    // undo a ball cam the player toggled with their own key a second ago.
    if (settings.ballCam !== this.lastBallCam) {
      this.lastBallCam = settings.ballCam;
      this.input.ballCam = settings.ballCam;
    }
    // Re-asked rather than remembered: `auto` is a question about the DEVICE,
    // and the answer can change under a session — a tablet docked to a
    // keyboard, or a window dragged to a touchscreen — without the setting
    // moving at all.
    this.touchWanted = touchControlsVisible(settings.touch.mode, pointerCapabilities());
    this.touch.setAutoThrottle(settings.touch.autoThrottle);
    this.syncTouch();
  }

  dispose(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.onResize);
    this.releaseAudioUnlock();
    // Stops the engine bed and every live one-shot. The shared mixer's graph
    // survives, so the next match does not pay for a new AudioContext — and
    // does not need a fresh gesture to start one.
    this.audio.dispose();
    this.skipInput.dispose();
    this.scoreboardKey.dispose();
    this.touch.dispose();
    this.netHud?.dispose();
    this.hud.dispose();
    this.scene.dispose();
    // `dispose` frees GPU resources but leaves the WebGL context alive, and a
    // browser allows only a handful; every rematch builds a new renderer.
    this.scene.renderer.forceContextLoss();
    this.scene.renderer.domElement.remove();
  }

  /**
   * Per-car one-shots that the world does not report as events.
   *
   * `WorldEvents` deliberately carries only what the VFX need, so a jump, a
   * landing and a pad pickup are all derived here from EDGES in the car state
   * — which is also why the previous values are kept per car rather than for
   * the player alone: an opponent's boost grab is a positional sound and part
   * of knowing where they are.
   */
  private playCarSounds(): void {
    for (let i = 0; i < this.world.cars.length; i++) {
      const car = this.world.cars[i];
      const wasOnGround = this.wasOnGround[i];
      const previousBoost = this.lastBoost[i];
      const wasDodging = this.wasDodging[i];
      const wasDemolished = this.wasDemolished[i];
      this.wasOnGround[i] = car.onGround;
      this.lastBoost[i] = car.boostAmount;
      this.wasDodging[i] = car.isDodging;
      this.wasDemolished[i] = car.demolished;

      // The demolition itself, on the edge INTO the state — and before the
      // `demolished` guard below, which is what stops a wreck from making
      // landing and pickup noises while it waits to respawn.
      if (!wasDemolished && car.demolished) {
        this.audio.playDemo(car.position);
        this.effects.demolition(toThree(car.position, this.vecB), car.team);
        // A demo is a hit you feel through the screen. Only the player's own,
        // and only theirs: shaking the camera because someone across the pitch
        // was wrecked reads as a bug.
        if (i === this.player) this.camera.shake(70, 0.7);
      }

      if (car.demolished) continue;

      // A flip is the most-used mechanic in the game after the throttle, so
      // its edge is worth the two words of state.
      if (!wasDodging && car.isDodging) this.audio.playDodge(car.position);

      // A pad is worth 12 (small) or 100 (big); anything smaller is the drain
      // of a car that is boosting, which is the engine bed's job, not a pip.
      const gained = car.boostAmount - previousBoost;
      if (gained >= 10) this.audio.playBoostPickup(gained > 50, car.position);

      // Leaving the ground with the jump key down is a jump; leaving it any
      // other way is a ramp, a wall or a bump, and none of those are one.
      if (wasOnGround && !car.onGround && this.lastInputs[i]?.jump) {
        this.audio.playJump(car.position);
      }
      if (!wasOnGround && car.onGround) {
        // Landing loudness comes from how hard the car arrived, so settling
        // onto the floor at walking pace makes almost nothing.
        this.audio.playLanding(impactStrength(Math.abs(car.velocity.z)), car.position);
      }
    }
  }

  /**
   * The kickoff pips: 3, 2, 1.
   *
   * Driven off `countdownNumber` rather than a timer of its own so it cannot
   * drift from the numeral the HUD is showing, and latched so a numeral that
   * spans several ticks is announced once. `countdownNumber` reads 0 outside
   * the countdown and never inside it, so the "go" is not here — it belongs to
   * the phase EDGE, and `watchPhase` plays it.
   */
  private playCountdownAudio(): void {
    const match = this.world.match;
    const n = match.phase === 'countdown' ? match.countdownNumber : -1;
    if (n === this.lastCountdownSpoken) return;
    this.lastCountdownSpoken = n;
    if (n > 0) this.audio.playCountdown(n);
  }

  /** The two phase edges the menu cares about: kickoff over, and full time. */
  private watchPhase(): void {
    const phase = this.world.match.phase;
    if (phase === this.lastPhase) return;
    const previous = this.lastPhase;
    this.lastPhase = phase;
    if (previous === 'countdown') {
      // `playCountdown(0)` is the "go" — an octave above the pips and twice as
      // long. Same frame the HUD flashes GO and control is handed over.
      this.audio.playCountdown(0);
      this.hooks.onCountdownComplete();
    }
    if (phase === 'ended') {
      this.audio.playWhistle();
      this.hooks.onMatchEnded(this.result());
    }
  }

  private result(): MatchResult {
    const match = this.world.match;
    const blue = match.score[0];
    const orange = match.score[1];
    const winner = blue === orange ? null : blue > orange ? 0 : 1;

    // Closed here rather than in the collector's constructor because `matches`,
    // `wins` and the MVP are all facts about the finished match, and `finish`
    // is idempotent so a second full-time cannot pay twice.
    this.stats?.finish(winner);
    // A spectated match (`bot-showcase`) has no local car to be paid for, so
    // there is nothing to award and the field stays absent.
    const stats =
      this.stats !== null && this.human !== null ? this.stats.statsFor(this.human) : undefined;

    return {
      score: [blue, orange],
      winner,
      playerTeam: this.team,
      overtime: match.inOvertime,
      goals: match.goals.map(g => ({
        team: g.team,
        clockRemaining: g.clockRemaining,
        speed: g.speed,
      })),
      ...(stats === undefined ? {} : { stats }),
    };
  }

  /**
   * One tick of a practice run. A no-op for every other kind of session.
   *
   * Everything the run itself decides is in `DrillSession`; what is left here
   * is the renderer's half. `ballScored` is narrowed to THIS team's goals on
   * the way in — an own goal is scored by the other team, and a drill that
   * congratulated a player for putting it in their own net would be worse than
   * one with no test at all.
   */
  private stepDrill(events: WorldEvents): void {
    const drill = this.drill;
    if (drill === null || drill.run.finished) return;

    const step = drill.step({
      ballScored: events.goal !== null && events.goal.team === this.team,
      ballTouched: events.touches > 0,
    });
    if (step === 'finished') {
      this.hooks.onDrillFinished(drill.run.summary());
      return;
    }
    if (step === 'placed') {
      // The last attempt's boost trails and tyre marks belong to a ball that
      // is no longer there, and the pose ring is a recording of it.
      this.effects.reset();
      this.replayBuffer.clear();
    }
  }

  private tick(dt: number, tickIndex: number): void {
    // A NETWORKED match cannot be paused, and pretending otherwise is worse
    // than not offering it: the server keeps stepping the room whatever this
    // client does, so a frozen tick here would simply stop reconciling and the
    // player would come back to a world several seconds in the future. The menu
    // therefore suspends INPUT — the car goes neutral and coasts, which is
    // exactly what the server does for a player it has stopped hearing from
    // (see `INPUT_HOLD_TICKS` in src/net/server-room.ts).
    if (this.paused && this.net === null) return;

    // A skip is applied BEFORE the tick, so the tick it lands on is already the
    // first tick of the new countdown and the renderer's reset happens exactly
    // once — on the same tick a replay that ran its course would have produced
    // it. `skipReplay` is a no-op in every other phase, so both detectors can
    // simply be fed every tick; `consume` must run every tick regardless, or a
    // press during play would still be sitting in the latch when a goal lands.
    const pressed = this.skipInput.consume();
    const held = this.skipLatch.update(this.input.anyInput());
    const skipped = (pressed || held) && this.world.skipReplay() !== null;

    // Bots decide against the world as it is NOW, before it is stepped, and all
    // of them in one call — see the class header on shared prediction.
    const inputs = this.director
      ? this.director.inputsFor(this.botView)
      : this.world.cars.map(() => neutralInput());

    // Input is sampled once per TICK, never per frame — see the note at the top
    // of src/input/controller.ts. It is sampled even while spectating, because
    // the ball-cam key is edge-latched inside `sample` and would otherwise stop
    // working the moment nobody was driving.
    const driven = this.world.cars[this.human ?? this.player];
    const input = this.paused ? neutralInput() : this.input.sample(!driven.onGround);
    if (this.human !== null) inputs[this.human] = input;
    this.lastInputs = inputs;

    for (let i = 0; i < this.world.cars.length; i++) {
      writeSnapshot(this.prevCars[i], this.world.cars[i]);
    }
    const swap = this.prevBall;
    this.prevBall = this.currBall;
    this.currBall = swap;

    const events = this.world.step(inputs, dt, tickIndex);
    // BEFORE the snapshots below, so a shot placed this tick is the pose the
    // renderer interpolates towards. After them, the reset would be invisible
    // for one tick and then jump.
    this.stepDrill(events);
    const kickoff = events.kickoff || skipped;
    this.feedDirector(events, kickoff);
    // After the step, on purpose: every shot and save predicate judges the
    // velocity a contact LEFT BEHIND, so the collector has to see the world the
    // tick produced. See the header of ./game/match-stats.
    if (events.detail) this.stats?.observe(events.detail, this.world.ball, dt);

    for (let i = 0; i < this.world.cars.length; i++) {
      writeSnapshot(this.currCars[i], this.world.cars[i]);
    }
    copy(this.currBall, this.world.ball.position);

    // Record BEFORE the goal is handled, so the window ends on the frame the
    // ball was actually across the line. Frozen ticks are skipped: they are the
    // same pose repeated, and letting them into the ring would pad the front of
    // the next replay with three seconds of a stationary field.
    if (!events.frozen) {
      this.replayBuffer.record(
        this.world.ball.position,
        this.world.ball.angularVelocity,
        this.world.cars,
        inputs
      );
    }

    for (const hit of events.hits) {
      this.effects.ballImpact(toThree(hit.position, new Vector3()), hit.strength);
      this.camera.shake(Math.min(55, hit.strength * 0.012));
      // `impactStrength` maps a closing speed onto 0..1; the hit event already
      // carries that speed, so the sound and the particles read the same number.
      this.audio.playBallHit(impactStrength(hit.strength), hit.position);
    }
    this.playCarSounds();
    if (events.goal) {
      const goalSign = Math.sign(this.world.ball.position.y) || 1;
      this.effects.goalExplosion(
        toThree({ x: 0, y: this.world.ball.position.y, z: GOAL.heightMesh / 2 }, new Vector3()),
        events.goal.team,
        goalSign
      );
      this.camera.shake(90, 0.9);
      // The crowd reacts to the same event as the particles. Without this the
      // stadium sits still through a goal, which reads as a bug even though
      // nothing is broken.
      this.scene.celebrateGoal(events.goal.team);
      this.audio.playGoalExplosion(this.world.ball.position);
      this.audio.playWhistle();
      // A drill has no replay. `drillMatchConfig` gives the replay phase a
      // zero duration, so the card would appear and vanish inside two ticks —
      // a flicker in the corner on every single goal.
      if (this.drill === null) this.beginReplay(events.goal);
      // Re-arm the skip from the goal itself: a player who was holding the
      // throttle when it went in must release and press again, or the replay
      // would end on the frame it started.
      this.skipLatch.reset();
      this.skipInput.clear();
    }
    if (kickoff) {
      this.effects.reset();
      this.replayBuffer.clear();
      this.clip = null;
      for (let i = 0; i < this.world.cars.length; i++) {
        writeSnapshot(this.prevCars[i], this.world.cars[i]);
        writeSnapshot(this.currCars[i], this.world.cars[i]);
      }
      copy(this.prevBall, this.world.ball.position);
      copy(this.currBall, this.world.ball.position);
      this.camera.reset(this.world.cars[this.player], this.world.ball, this.input.ballCam);
    }

    this.playCountdownAudio();
    this.watchPhase();
  }

  /**
   * Choose the window to play back, and point the replay camera at it.
   *
   * The scorer comes from `MatchState.goals` rather than from the step event,
   * because `WorldEvents.goal` deliberately carries only what the VFX need and
   * the credited car is a scoreboard fact.
   */
  private beginReplay(goal: { team: 0 | 1; speed: number }): void {
    const match = this.world.match;
    const scored = match.goals[match.goals.length - 1];
    const scorer = scored ? scored.scorer : null;

    this.clip = {
      window: replayWindow(this.replayBuffer, this.replayBuffer.newest - 1),
      scorer: scorerLabel(scorer, goal.team, this.human, this.world.cars.length / 2),
      speed: goal.speed,
    };
    this.replayCamera.begin(goal.team, scorer, this.world.ball.position.x);
  }

  /**
   * Hand the step's events to the bots' prediction cache, then wipe their
   * per-episode memory on a kickoff.
   *
   * `reset` after `observe` rather than instead of it: a kickoff tick is also
   * the tick the ball teleports back to centre, and the ordering keeps the two
   * concerns — "the cached trajectory is wrong" and "the intent, aim bias and
   * input queue belong to a play that is over" — from depending on each other.
   * A rematch gets a brand-new director, so nothing survives a match either.
   */
  private feedDirector(events: WorldEvents, kickoff: boolean): void {
    if (!this.director) return;
    this.predictionEvents.ballTouches.length = events.touches;
    this.predictionEvents.kickoff = kickoff;
    this.predictionEvents.frozen = events.frozen;
    this.predictionEvents.goal = events.goal;
    this.director.observe(this.predictionEvents);
    if (kickoff) this.director.reset();
  }

  private render(alpha: number, frameTime: number): void {
    const dt = Math.min(0.25, Math.max(0, frameTime));

    // The whole frame branches on one question: is a replay on screen? When it
    // is, the live world is a frozen tableau nobody should be looking at, and
    // every visual is driven from the recorded pose instead.
    const playback = this.playbackTime(alpha);
    const camera = playback === null ? this.camera.camera : this.replayCamera.camera;
    this.scene.resize(camera);

    if (playback === null) {
      this.updateCars(alpha, dt);
      this.updateBall(alpha, dt);
      this.fillInterpolated(alpha);
      this.camera.update(this.interpCar, this.interpBall, this.input.ballCam, frameTime);
      this.scene.lighting.focus(this.carMeshes[this.player].group.position);
      this.updateEffects(dt, camera, null);
    } else {
      const pose = this.replayPose;
      this.replayBuffer.sample(playbackFrame(this.clip!.window, playback, this.replayDuration), pose);
      this.updateCarsFromPose(pose, dt);
      this.updateBallFromPose(pose, dt);
      this.replayCamera.update(playback, pose, frameTime);
      this.scene.lighting.focus(this.scene.ball.group.position);
      this.updateEffects(dt, camera, pose);
    }

    this.updateBoostPads();
    this.updateAudio(camera, dt, playback);
    this.updateHud(playback, camera);
    this.updateNetHud();

    this.scene.render(camera);
  }

  /**
   * Put the listener where the camera is, and drive the engine bed.
   *
   * The listener follows the CAMERA rather than the car: what the player hears
   * has to agree with what they see, and in ball cam those two sit a car
   * length and a half apart. The engine follows the car being DRIVEN, with its
   * live input, so the throttle blip is there — a bed driven from speed alone
   * is a car that never revs against a wall.
   *
   * A replay is silent apart from the bed: the one-shots that made the goal
   * already played live, and firing them again from the recording would
   * double every touch the replay contains.
   */
  private updateAudio(camera: PerspectiveCamera, dt: number, playback: number | null): void {
    camera.getWorldDirection(this.listenerForward);
    this.listenerUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    this.audio.setListener(
      fromThree(camera.position),
      fromThree(this.listenerForward),
      fromThree(this.listenerUp)
    );

    // Spectating has no car of yours to be the engine you hear, so the bed
    // follows the camera's car instead — which is the one on screen.
    const seat = this.human ?? this.player;
    const heard = this.world.cars[seat];
    if (!heard || heard.demolished || this.paused) {
      this.audio.stopEngine();
      this.audio.playBoost(false);
      this.audio.playSupersonic(false);
      return;
    }
    const input = playback === null ? this.lastInputs[seat] : undefined;
    this.audio.updateEngine(heard, dt, input);

    // The two continuous layers are driven by STATE rather than by edges:
    // both calls are idempotent (playing one that is already playing returns
    // immediately), so handing them the current truth every frame is both
    // correct and cheaper than tracking two more booleans. During a replay the
    // live car's input is meaningless, so the jet is silenced rather than
    // stuck on from whatever the player was holding when the goal went in.
    const boosting =
      playback === null && (input?.boost ?? false) && heard.boostAmount > 0;
    this.audio.playBoost(boosting);
    this.audio.playSupersonic(playback === null && heard.supersonic);
  }

  /**
   * The off-screen ball arrow's placement, or null when it should not show.
   *
   * Only during live play: a replay has its own camera doing deliberate things
   * with framing, and an arrow chasing the ball around the edge of a cinematic
   * is noise. The countdown is excluded for the same reason — the ball is on
   * the spot and everyone knows where it is.
   */
  private ballArrow(camera: PerspectiveCamera, playback: number | null): BallPointer | null {
    if (playback !== null) return null;
    const phase = this.world.match.phase;
    if (phase !== 'playing' && phase !== 'overtime' && phase !== 'overtime-pending') return null;

    // `project` mutates in place and needs the camera's matrices current; the
    // scene's own render does that a few lines later, so it is forced here.
    camera.updateMatrixWorld();
    const ndc = this.ballNdc.copy(this.scene.ball.group.position).project(camera);
    return ballPointer(ndc.x, ndc.y, ndc.z > 1);
  }

  /** The ping pill and the held scoreboard. A no-op offline. */
  private updateNetHud(): void {
    const net = this.net;
    const hud = this.netHud;
    if (net === null || hud === null) return;
    const room = net.roomState;
    const localId = net.playerId;
    const players: NetPlayerRow[] = (room?.players ?? []).map(p => ({
      playerId: p.playerId,
      name: p.displayName,
      team: p.team,
      carIndex: p.carIndex,
      car: p.car,
      ready: p.ready,
      isBot: p.isBot,
      ping: p.ping,
      isLocal: p.playerId === localId,
    }));
    const score = this.world.match.score;
    hud.update({
      pingMs: net.rttMs,
      degraded: net.degraded,
      lead: net.stats.lead,
      staleMs: net.staleMs(),
      players,
      score: [score[0], score[1]],
      teams: room?.mode !== 'freeplay',
    });
  }

  /** The match's configured replay length, which the schedule stretches onto. */
  private get replayDuration(): number {
    return this.world.match.config.replayDuration;
  }

  /**
   * Seconds into the replay, or null when nothing is being replayed.
   *
   * `phaseTimer` counts DOWN once per tick, so `alpha * DT` is added back to
   * carry it smoothly across the fraction of a tick the frame is standing in —
   * without it the replay clock steps at 120 Hz and the slow-mo, which is where
   * every frame counts, visibly judders.
   */
  private playbackTime(alpha: number): number | null {
    if (this.world.match.phase !== 'replay') return null;
    const clip = this.clip;
    if (!clip || clip.window.end <= clip.window.start) return null;
    const elapsed = this.replayDuration - this.world.match.phaseTimer + alpha * DT;
    return Math.max(0, Math.min(this.replayDuration, elapsed));
  }

  private updateCars(alpha: number, dt: number): void {
    for (let i = 0; i < this.carMeshes.length; i++) {
      const car = this.world.cars[i];
      const mesh = this.carMeshes[i];

      lerpToThree(this.prevCars[i].position, this.currCars[i].position, alpha, this.vecA);
      mesh.group.position.copy(this.vecA);
      mesh.group.quaternion.copy(this.prevCars[i].quaternion).slerp(this.currCars[i].quaternion, alpha);
      mesh.setVisible(!car.demolished);
      mesh.setSupersonic(car.supersonic && !car.demolished);

      const forwardSpeed = dot(carForward(car.orientation), car.velocity);
      const steer = this.lastInputs[i]?.steer ?? 0;
      mesh.updateWheels(forwardSpeed, steer, this.world.powerslide(i), dt);
      mesh.group.updateMatrixWorld();
    }
  }

  private updateBall(alpha: number, dt: number): void {
    lerpToThree(this.prevBall, this.currBall, alpha, this.vecA);
    this.scene.ball.group.position.copy(this.vecA);
    this.spinBall(this.world.ball.angularVelocity, dt);
    this.scene.ball.update(this.vecA.y);
  }

  /** Car meshes driven from a recorded frame instead of the live world. */
  private updateCarsFromPose(pose: ReplayPose, dt: number): void {
    for (let i = 0; i < this.carMeshes.length; i++) {
      const car = pose.cars[i];
      const mesh = this.carMeshes[i];
      if (!car) continue;

      mesh.group.position.copy(toThree(car.position, this.vecA));
      mesh.group.quaternion.copy(car.quaternion);
      mesh.setVisible(!car.demolished);
      mesh.setSupersonic(car.supersonic && !car.demolished);
      mesh.updateWheels(car.forwardSpeed, car.steer, 0, dt);
      mesh.group.updateMatrixWorld();
    }
  }

  private updateBallFromPose(pose: ReplayPose, dt: number): void {
    this.scene.ball.group.position.copy(toThree(pose.ball, this.vecA));
    this.spinBall(pose.ballSpin, dt);
    this.scene.ball.update(this.vecA.y);
  }

  /**
   * Advance the ball's visual spin about the converted angular-velocity axis.
   * Renormalized every frame, so no drift accumulates in the orientation.
   * `BallState` carries none of its own — the physics does not need one.
   */
  private spinBall(angularVelocity: Vec3, dt: number): void {
    const omega = toThree(angularVelocity, this.vecB);
    const rate = omega.length();
    if (rate <= 1e-6) return;
    this.spinDelta.setFromAxisAngle(omega.divideScalar(rate), rate * dt);
    this.ballSpin.premultiply(this.spinDelta).normalize();
    this.scene.ball.mesh.quaternion.copy(this.ballSpin);
  }

  /**
   * Per-car effects, from the live world or from a replayed pose.
   *
   * A replayed car deliberately reports `onGround: false` and no powerslide:
   * the tyre marks it laid are still on the pitch from the live play, and
   * re-laying them from the replay would double every skid the goal contained.
   * Everything else — the boost flame, the trail, the supersonic streaks — is
   * driven from the recording, which is what makes the replay look like the
   * play rather than like a diagram of it.
   */
  private updateEffects(dt: number, camera: PerspectiveCamera, pose: ReplayPose | null): void {
    for (let i = 0; i < this.carMeshes.length; i++) {
      const mesh = this.carMeshes[i];
      const replayed = pose?.cars[i];
      const car = this.world.cars[i];

      const state: CarEffectState = replayed
        ? {
            position: mesh.group.position,
            quaternion: mesh.group.quaternion,
            velocity: this.vecB.set(0, 0, 0),
            nozzle: mesh.boostAnchor.getWorldPosition(this.nozzle),
            boosting: replayed.boosting,
            supersonic: replayed.supersonic,
            onGround: false,
            powerslide: 0,
            speed: replayed.speed,
            demolished: replayed.demolished,
          }
        : {
            position: mesh.group.position,
            quaternion: mesh.group.quaternion,
            velocity: toThree(car.velocity, this.vecB),
            nozzle: mesh.boostAnchor.getWorldPosition(this.nozzle),
            boosting: (this.lastInputs[i]?.boost ?? false) && car.boostAmount > 0 && !car.demolished,
            supersonic: car.supersonic,
            onGround: car.onGround,
            powerslide: this.world.powerslide(i),
            speed: length(car.velocity),
            demolished: car.demolished,
          };
      this.effects.updateCar(i, state, dt);
    }

    this.effects.update(
      dt,
      camera,
      this.scene.viewportHeight,
      pose ? 0 : length(this.world.cars[this.player].velocity)
    );
  }

  private updateBoostPads(): void {
    const pads = this.scene.arena.boostPads;
    for (let i = 0; i < 34; i++) pads.setActive(i, this.world.padActive(i));
    pads.update(performance.now() / 1000);
  }

  private updateHud(playback: number | null, camera: PerspectiveCamera): void {
    const match = this.world.match;
    const lastGoal = match.goals[match.goals.length - 1];
    // The banner belongs to the celebration only. Rocket League clears it when
    // the replay starts, and it has to: the replay card names the same scorer
    // and shot speed in the corner, so leaving the banner up double-reports it
    // across the middle of the shot the replay exists to show.
    const showBanner = match.phase === 'goal' && lastGoal;
    const clip = this.clip;
    this.hud.update({
      score: match.score,
      clock: match.formatClock(),
      clockSeconds: match.clock,
      phase: match.phase,
      overtime: match.inOvertime,
      // Spectating shows no boost ring: `this.player` is only the CAMERA's
      // seat there, and a meter for a car you cannot drive reads as broken.
      boost: this.human === null ? null : this.world.cars[this.human].boostAmount,
      countdown: match.countdownNumber,
      goal: showBanner ? { team: lastGoal.team, speed: lastGoal.speed } : null,
      flash: this.effects.screenFlash,
      speed: this.effects.speedIntensity,
      replay:
        playback !== null && clip
          ? {
              scorer: clip.scorer,
              speed: clip.speed,
              progress: this.replayDuration > 0 ? playback / this.replayDuration : 0,
            }
          : null,
      glow: this.effects.fireworks.wash,
      // Hidden while spectating (no car is "yours") and during replays, where
      // the live car is a frozen tableau and its speed is a stale number.
      speedo:
        this.human !== null && playback === null
          ? {
              speed: length(this.world.cars[this.human].velocity),
              supersonic: this.world.cars[this.human].supersonic,
            }
          : null,
      ballArrow: this.ballArrow(camera, playback),
      // Only your OWN wreck, and never over a replay: the replay is showing a
      // goal, and a countdown for a car that was demolished in a different
      // moment on top of it belongs to neither.
      respawnIn:
        this.human !== null && playback === null && this.world.cars[this.human].demolished
          ? this.world.cars[this.human].respawnTimer
          : null,
      scoreboard: this.scoreboardMarkup(),
    });
    this.hud.setDrill(this.drillHudState());
  }

  /**
   * The practice panel's contents, or null for every other kind of session.
   *
   * The clock reads FULL during the beat between attempts rather than counting
   * down through it. The run's own clock is not moving then — see `stepDrill`
   * — and a bar that drained while the ball was still being placed would be
   * telling the player they were losing time they had not been given yet.
   */
  private drillHudState(): DrillHudState | null {
    const drill = this.drill;
    if (drill === null) return null;
    const run = drill.run;
    const limit = run.spec.limitSeconds;
    return {
      name: run.spec.name,
      attempt: run.attempt,
      attempts: DRILL_ATTEMPTS,
      hits: run.hits,
      remaining: drill.holding || limit <= 0 ? 1 : run.secondsLeft / limit,
    };
  }

  /**
   * The held scoreboard's markup, or null when it should not be on screen.
   *
   * Offline only, because the stats behind it are: `MatchStatsCollector` is
   * not built for a networked world (a client that scored its own numbers
   * could edit them), and a board of zeroes is worse than no board. Networked
   * matches already have their own held scoreboard in ./ui/net-hud, driven by
   * the room state the server sends.
   *
   * Built only on the frames the key is down — it allocates a string per call,
   * and the HUD's diff means an unchanged board costs nothing beyond that.
   */
  private scoreboardMarkup(): string | null {
    const stats = this.stats;
    if (stats === null || !this.scoreboardKey.isHeld) return null;

    const teamSize = Math.max(1, this.world.cars.length / 2);
    const all = stats.all();
    const entries: ScoreboardEntry[] = this.world.cars.map((car, i) => ({
      carIndex: i,
      team: car.team,
      // The same positional naming the replay card uses, so "Blue 2" means the
      // same car in both places.
      name: scorerLabel(i, car.team, this.human, teamSize),
      isLocal: i === this.human,
      stats: all[i],
    }));
    const score = this.world.match.score;
    return scoreboardMarkup(entries, [score[0], score[1]]);
  }

  /** Copy the interpolated player and ball transforms into the camera inputs. */
  private fillInterpolated(alpha: number): void {
    const car = this.world.cars[this.player];
    const prev = this.prevCars[this.player].position;
    const curr = this.currCars[this.player].position;
    this.interpCar.position.x = prev.x + (curr.x - prev.x) * alpha;
    this.interpCar.position.y = prev.y + (curr.y - prev.y) * alpha;
    this.interpCar.position.z = prev.z + (curr.z - prev.z) * alpha;
    this.interpCar.velocity = car.velocity;
    this.interpCar.orientation = car.orientation;
    this.interpCar.demolished = car.demolished;
    this.interpCar.supersonic = car.supersonic;

    this.interpBall.position.x = this.prevBall.x + (this.currBall.x - this.prevBall.x) * alpha;
    this.interpBall.position.y = this.prevBall.y + (this.currBall.y - this.prevBall.y) * alpha;
    this.interpBall.position.z = this.prevBall.z + (this.currBall.z - this.prevBall.z) * alpha;
  }
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

const container = document.getElementById('app');
if (!container) throw new Error('#app container missing');

// The menu owns the lifecycle now: it loads (or repairs) the stored settings,
// then builds and tears down one `App` per match. A corrupt localStorage entry
// costs the player their settings and nothing else — `loadSettings` never
// throws, so it can never stop the game booting.
//
// The `learned` tier is loaded PESSIMISTICALLY here: whether a trained policy
// exists is an IndexedDB question, and an asynchronous answer cannot gate the
// boot. So a stored `learned` is scrubbed on the way in, and the menu restores
// it — through the same `resolveDifficulty` — once its checkpoint store has
// answered. "Not yet known" and "not there" behave identically, which is the
// only safe reading of a value that would otherwise throw.
const { settings } = loadSettings();
new MenuOverlay(container, settings, {
  createSession: (chosen, hooks, options) => {
    const app = new App(container, chosen, hooks, options);
    app.start();
    return app;
  },
});
